import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import {
  Bell,
  ShieldCheck,
  Settings as SettingsIcon,
  Save,
  Key,
  QrCode,
  Link2,
  Slack,
  BarChart4,
  Search,
  Mail,
  Github,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string().min(8, "Password must be at least 8 characters"),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

const ACCENT = "var(--accent, #8B5CF6)";

// ── Solid settings card (reskinned chrome) ──
const SettingsCard: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <section className="mb-5 rounded-2xl border border-white/[0.07] bg-og01-card p-6">
    <div className="mb-5 flex items-center gap-3">
      <span className="grid h-[34px] w-[34px] flex-none place-items-center rounded-[9px]" style={{ background: `color-mix(in srgb, ${ACCENT} 14%, transparent)`, color: ACCENT }}>
        {icon}
      </span>
      <h3 className="font-heading text-[20px] text-og01-text">{title}</h3>
    </div>
    {children}
  </section>
);

const SettingsRow: React.FC<{ label: string; description: string; children: React.ReactNode }> = ({ label, description, children }) => (
  <div className="flex items-center justify-between gap-4 border-t border-white/[0.07] py-4 first:border-t-0 first:pt-0">
    <div>
      <div className="text-sm font-medium text-og01-text">{label}</div>
      <div className="mt-0.5 text-[12.5px] text-og01-text-muted">{description}</div>
    </div>
    {children}
  </div>
);

const IntegrationRow: React.FC<{ icon: React.ReactNode; bgColor: string; label: string; description: string; checked: boolean; onChange: (v: boolean) => void }> = ({ icon, bgColor, label, description, checked, onChange }) => (
  <div className="flex items-center gap-3 border-t border-white/[0.07] py-[15px] first:border-t-0 first:pt-0">
    <div className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[9px] text-white" style={{ background: bgColor }}>{icon}</div>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold text-og01-text">{label}</div>
      <div className="mt-px text-[12.5px] text-og01-text-muted">{description}</div>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);

const Settings = () => {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [darkModeEnabled, setDarkModeEnabled] = useState(true);
  const [compactModeEnabled, setCompactModeEnabled] = useState(false);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(true);
  const [language, setLanguage] = useState("English");
  const [showTwoFactorDialog, setShowTwoFactorDialog] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);

  const [slackEnabled, setSlackEnabled] = useState(false);
  const [googleSearchConsoleEnabled, setGoogleSearchConsoleEnabled] = useState(false);
  const [googleAnalyticsEnabled, setGoogleAnalyticsEnabled] = useState(false);
  const [githubEnabled, setGithubEnabled] = useState(false);
  const [mailchimpEnabled, setMailchimpEnabled] = useState(false);

  const passwordForm = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });

  const handleSaveChanges = () => toast.success("Settings saved successfully");
  const handleUpdatePassword = (values: z.infer<typeof passwordSchema>) => {
    console.log("Password update:", values);
    toast.success("Password updated successfully");
    passwordForm.reset();
  };
  const handleTwoFactorToggle = (checked: boolean) => {
    if (checked) setShowTwoFactorDialog(true);
    else { setTwoFactorEnabled(false); toast.success("Two-factor authentication disabled"); }
  };
  const setup2FA = () => { setTwoFactorEnabled(true); setShowTwoFactorDialog(false); toast.success("Two-factor authentication enabled"); };
  const handleIntegrationToggle = (name: string, enabled: boolean) => toast.success(`${name} integration ${enabled ? "enabled" : "disabled"}`);

  const inputClass = "w-full rounded-[10px] border border-white/[0.07] bg-[#0F0F18] px-3.5 py-3 text-sm text-og01-text outline-none transition-colors placeholder:text-og01-text-muted focus:border-[#8B5CF6]/60";

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden" style={{ background: "#08080E" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0, background: "radial-gradient(620px 420px at 88% -6%, rgba(139,92,246,.15), transparent 70%), radial-gradient(560px 460px at 102% 108%, rgba(255,122,24,.10), transparent 70%)" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.4, zIndex: 0, backgroundImage: "linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)", backgroundSize: "46px 46px", WebkitMaskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)", maskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)" }} />

      <div className="relative z-[1] mx-auto px-6 py-6 md:px-10 lg:px-14" style={{ ["--accent" as string]: "#8B5CF6", maxWidth: "880px" } as React.CSSProperties}>
        {/* Header */}
        <div className="mb-6 flex items-start gap-4">
          <div className="mt-0.5 grid h-[38px] w-[38px] flex-none place-items-center rounded-[10px]" style={{ background: `color-mix(in srgb, ${ACCENT} 14%, transparent)` }}>
            <SettingsIcon className="h-5 w-5" style={{ color: ACCENT }} />
          </div>
          <div className="flex-1">
            <h1 className="font-heading text-[32px] leading-none text-og01-text">Settings</h1>
            <p className="mt-1 text-[13.5px] text-og01-text-secondary">Manage your application preferences</p>
          </div>
          <button onClick={handleSaveChanges}
            className="inline-flex items-center gap-2 rounded-[10px] border-none px-4 py-2.5 text-[13.5px] font-semibold text-white transition-all hover:brightness-105"
            style={{ background: "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)", boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}>
            <Save className="h-[15px] w-[15px]" />Save Changes
          </button>
        </div>

        {/* Password */}
        <SettingsCard icon={<Key className="h-[18px] w-[18px]" />} title="Password Settings">
          <form onSubmit={passwordForm.handleSubmit(handleUpdatePassword)} className="space-y-4">
            {(["currentPassword", "newPassword", "confirmPassword"] as const).map((field) => (
              <div key={field}>
                <label className="mb-2 block text-[13px] text-og01-text-secondary">
                  {field === "currentPassword" ? "Current Password" : field === "newPassword" ? "New Password" : "Confirm New Password"}
                </label>
                <input type="password" placeholder="••••••••" className={inputClass} {...passwordForm.register(field)} />
                {field === "newPassword" && !passwordForm.formState.errors.newPassword && (
                  <p className="mt-1.5 text-xs text-og01-text-muted">Minimum 8 characters.</p>
                )}
                {passwordForm.formState.errors[field] && (
                  <p className="mt-1.5 text-xs" style={{ color: "#EF4444" }}>{passwordForm.formState.errors[field]?.message}</p>
                )}
              </div>
            ))}
            <button type="submit"
              className="w-full rounded-[10px] border-none py-[13px] text-sm font-semibold text-white transition-all hover:brightness-105"
              style={{ background: "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)", boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}>
              Update Password
            </button>
          </form>
        </SettingsCard>

        {/* Security */}
        <SettingsCard icon={<ShieldCheck className="h-[18px] w-[18px]" />} title="Authentication & Security">
          <button className="mb-1 flex w-full items-center justify-center gap-2.5 rounded-[10px] border border-white/[0.07] bg-[#0F0F18] py-3 text-sm font-semibold text-og01-text transition-colors hover:border-white/[0.12] hover:bg-og01-card-hover">
            <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Sign in with Google
          </button>
          <SettingsRow label="Two-Factor Authentication" description="Add an extra layer of security to your account">
            <Switch checked={twoFactorEnabled} onCheckedChange={handleTwoFactorToggle} />
          </SettingsRow>
        </SettingsCard>

        {/* Integrations */}
        <SettingsCard icon={<Link2 className="h-[18px] w-[18px]" />} title="Integrations">
          <IntegrationRow icon={<Slack className="h-[18px] w-[18px]" />} bgColor="#4A154B" label="Slack" description="Connect with your Slack workspace"
            checked={slackEnabled} onChange={(v) => { setSlackEnabled(v); handleIntegrationToggle("Slack", v); }} />
          <IntegrationRow icon={<Search className="h-[18px] w-[18px]" />} bgColor="#4285F4" label="Google Search Console" description="Link your Search Console data"
            checked={googleSearchConsoleEnabled} onChange={(v) => { setGoogleSearchConsoleEnabled(v); handleIntegrationToggle("Google Search Console", v); }} />
          <IntegrationRow icon={<BarChart4 className="h-[18px] w-[18px]" />} bgColor="#E37400" label="Google Analytics" description="Import your analytics data"
            checked={googleAnalyticsEnabled} onChange={(v) => { setGoogleAnalyticsEnabled(v); handleIntegrationToggle("Google Analytics", v); }} />
          <IntegrationRow icon={<Github className="h-[18px] w-[18px]" />} bgColor="#161614" label="GitHub" description="Connect to your repositories"
            checked={githubEnabled} onChange={(v) => { setGithubEnabled(v); handleIntegrationToggle("GitHub", v); }} />
          <IntegrationRow icon={<Mail className="h-[18px] w-[18px]" style={{ color: "#161614" }} />} bgColor="#FFE01B" label="Mailchimp" description="Sync your email campaigns"
            checked={mailchimpEnabled} onChange={(v) => { setMailchimpEnabled(v); handleIntegrationToggle("Mailchimp", v); }} />
        </SettingsCard>

        {/* General */}
        <SettingsCard icon={<SettingsIcon className="h-[18px] w-[18px]" />} title="General Settings">
          <SettingsRow label="Dark Mode" description="Toggle between dark and light theme">
            <Switch checked={darkModeEnabled} onCheckedChange={setDarkModeEnabled} />
          </SettingsRow>
          <SettingsRow label="Compact Mode" description="Reduce the spacing in the UI">
            <Switch checked={compactModeEnabled} onCheckedChange={setCompactModeEnabled} />
          </SettingsRow>
          <SettingsRow label="Language" description="Select your preferred language">
            <div className="relative">
              <button onClick={() => setShowLangDropdown(!showLangDropdown)}
                className="flex min-w-[140px] items-center justify-between gap-2 rounded-[10px] border border-white/[0.07] bg-[#0F0F18] px-3.5 py-2.5 text-[13.5px] text-og01-text transition-colors hover:border-white/[0.12]">
                {language}<ChevronDown className="h-[15px] w-[15px] text-og01-text-muted" />
              </button>
              {showLangDropdown && (
                <div className="absolute right-0 top-full z-50 mt-1 w-[140px] overflow-hidden rounded-[10px] border border-white/[0.12] bg-og01-card shadow-xl">
                  {["English", "Spanish", "French", "German", "Chinese"].map((lang) => (
                    <button key={lang} onClick={() => { setLanguage(lang); setShowLangDropdown(false); }}
                      className="w-full px-3.5 py-2 text-left text-sm transition-colors hover:bg-white/[0.04]"
                      style={{ color: lang === language ? "var(--accent)" : "#ABABC8" }}>
                      {lang}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </SettingsRow>
        </SettingsCard>

        {/* Notifications */}
        <SettingsCard icon={<Bell className="h-[18px] w-[18px]" />} title="Notifications">
          <SettingsRow label="Email Notifications" description="Receive notifications via email">
            <Switch checked={emailNotificationsEnabled} onCheckedChange={setEmailNotificationsEnabled} />
          </SettingsRow>
          <SettingsRow label="Push Notifications" description="Receive notifications on your device">
            <Switch checked={pushNotificationsEnabled} onCheckedChange={setPushNotificationsEnabled} />
          </SettingsRow>
        </SettingsCard>
      </div>

      {/* 2FA Dialog */}
      {showTwoFactorDialog && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-5" onClick={() => setShowTwoFactorDialog(false)}>
          <div className="absolute inset-0" style={{ background: "rgba(4,4,10,0.72)", backdropFilter: "blur(5px)" }} />
          <div className="relative w-full max-w-[400px] rounded-[18px] border border-white/[0.07] bg-og01-card p-7 text-center" onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "0 30px 80px -30px rgba(0,0,0,.7)" }}>
            <div className="mx-auto mb-3.5 grid h-[46px] w-[46px] place-items-center rounded-xl" style={{ background: `color-mix(in srgb, ${ACCENT} 14%, transparent)` }}>
              <ShieldCheck className="h-[23px] w-[23px]" style={{ color: ACCENT }} />
            </div>
            <h3 className="mb-1.5 font-heading text-[23px] text-og01-text">Enable Two-Factor Authentication</h3>
            <p className="mb-[18px] text-[13px] leading-relaxed text-og01-text-secondary">Scan the QR code with your authenticator app, or enter the setup code manually.</p>
            <div className="mx-auto mb-4 grid h-[168px] w-[168px] place-items-center rounded-xl bg-white p-3">
              <QrCode className="h-full w-full" style={{ color: "#08080E" }} />
            </div>
            <div className="mb-5 rounded-[9px] border border-white/[0.07] bg-[#0F0F18] p-2.5 text-[13px] tracking-[0.06em] text-og01-text-secondary" style={{ fontFamily: "'Geist Mono', ui-monospace, monospace" }}>
              JBSW Y3DP EHPK 3PXP
            </div>
            <div className="flex gap-2.5">
              <button onClick={() => setShowTwoFactorDialog(false)}
                className="flex-1 rounded-[10px] border border-white/[0.07] bg-og01-card py-3 text-sm font-semibold text-og01-text transition-colors hover:border-white/[0.12]">
                Cancel
              </button>
              <button onClick={setup2FA}
                className="flex flex-1 items-center justify-center gap-2 rounded-[10px] border-none py-3 text-sm font-semibold text-white transition-all hover:brightness-105"
                style={{ background: "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)", boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}>
                <ShieldCheck className="h-4 w-4" />Enable 2FA
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
